import { v4 as uuidV4 } from "uuid";

import type { EnvironmentsConfig } from "src/baseConnectionNode/types";
import { DataRetentionUnit } from "src/connections/types";
import type {
  OutputMappingBE,
  SingleMappingBE,
} from "src/integrationNode/types";

export const DEFAULT_CUSTOM_ERROR_CODE = `def validate(response_body, response_headers) -> None:
    """
    Modify the request to add response validation.

    Args:
        response_body: The value to be validated. Can be str, int, float, bool, None, dict, or list.
        response_headers: The response headers as a dictionary of strings.

    Available modules: hmac, hashlib, base64, json, math, re, datetime, time

    Available helpers:
        # Throw a custom provider exception
        throw_custom_error(message, code, error_type)

    Available error types: provider_error, provider_unavailable, provider_unauthorized, provider_missing_credentials, provider_invalid_request, provider_unexpected_format, provider_unexpected_status_code, response_entity_not_exists, response_resource_unavailable, provider_unknown
    """
    # Example: throw an error if the status code is not 200
    status = response_body.get("status")
    if status != 200:
        throw_custom_error("Bad request", 400, "provider_error")
`;

export const HttpVerbs = [
  "get",
  "post",
  "patch",
  "delete",
  "options",
  "head",
  "put",
] as const;

export type HttpVerb = (typeof HttpVerbs)[number];

export type KeyValuePair = {
  id_key: string;
  id_value: string;
  key: string;
  value: string;
  // When enabled, the parameter is omitted from the request if its value
  // resolves to empty. Only surfaced for query parameters.
  ignore_if_empty?: boolean;
};

export const getEmptyKeyValuePair = (): KeyValuePair => ({
  id_key: uuidV4(),
  id_value: uuidV4(),
  key: "",
  value: "",
});

export type BodyOption = "key_values" | "raw" | "jinja_template";

export type JinjaBodyContentType = "json" | "xml" | "raw";

export type PostprocessingType =
  | "camel_to_snake"
  | "kebab_to_snake"
  | "xml_to_json";

export type FileExtractionOperation = "attribute_extraction" | "content";

export type FileExtractionEncoding = "base64" | "gzip";

// Kept flat (rather than split into parallel rule/target dicts keyed by
// file_key) because users author both halves here and syncing two keyed dicts
// via file_key alone is brittle.
export type FileExtractionEntry = {
  id: string;
  file_key: { id: string; value: string };
  output: { id: string; mapped_to: string };
  operation: FileExtractionOperation;
  path?: { id: string; value: string };
  encodings?: FileExtractionEncoding[];
  default_content_type: { id: string; value: string };
  required: boolean;
  strip_file_data_from_response: boolean;
};

export type FileExtractionConfig = {
  active: boolean;
  extractions: FileExtractionEntry[];
};

export const getDefaultFileExtractionEntry = (): FileExtractionEntry => ({
  id: uuidV4(),
  file_key: { id: uuidV4(), value: "" },
  output: { id: uuidV4(), mapped_to: "" },
  operation: "attribute_extraction",
  path: { id: uuidV4(), value: "" },
  encodings: [],
  default_content_type: { id: uuidV4(), value: "" },
  required: false,
  strip_file_data_from_response: true,
});

export const getDefaultFileExtractionConfig = (): FileExtractionConfig => ({
  active: false,
  extractions: [],
});

export type BodyValue =
  | {
      selected: "raw";
      raw: string;
    }
  | {
      selected: "key_values";
      key_values: KeyValuePair[];
    }
  | {
      selected: "jinja_template";
      template: string;
      source: SingleMappingBE;
      content_type: JinjaBodyContentType;
    };

export type CustomConnectionConfig = {
  environments_config?: EnvironmentsConfig;
  testing: {
    use_cached_reports: boolean;
    // Deprecated, keeping for backwards compatibility
    use_fallback_live_connection: boolean;
  };
  caching: {
    active: boolean;
    max_age_seconds: string;
    // Used only for displaying in the UI
    // to properly convert seconds to the correct unit
    unit?: DataRetentionUnit;
  };
  timeout?: {
    active: boolean;
    timeout_seconds: string;
  };
  retry_config?: {
    active: boolean;
  };
  postprocessing_config?: {
    active: boolean;
    type?: PostprocessingType;
  };
  response_validation_config?: {
    active: boolean;
    // Snapshot of the flow's output schema
    response_schema?: Record<string, unknown> | null;
  };
  file_extraction_config?: FileExtractionConfig;
  custom_error_config?: {
    active: boolean;
    code?: string;
  };
  error: {
    allow_4xx: boolean;
    allow_5xx: boolean;
  };
  has_raw_requests_enabled_in_node: boolean;
};

export type CustomConnectionForm = {
  verb: HttpVerb;
  path: SingleMappingBE;
  params: KeyValuePair[];
  headers: KeyValuePair[];
  body: { id: string; value: BodyValue };
  response: OutputMappingBE;
  config: CustomConnectionConfig;
};
